import { useRef } from 'react';
import { getFramework, IExportLayerOriginInfo, trackConfig, useIdentity } from '@/framework';
import type { ITouchKitEvent } from '@/utils/event-kit';
import { BaseLayerViewType, LayerType, type ILayerProps } from '@/version-types';
import { MouseOperate } from '../../mouse-operate';
import { SnapCandidateResolver, type MoveSnapSlot } from '../../operation-events';
import { useStickyInfo } from './use-sticky-info';

export const useMoveOperate = () => {
    const identity = useIdentity();
    const oldInfo = useRef<ILayerProps>();
    const originLayerInfo = useRef<IExportLayerOriginInfo>();
    const framework = getFramework(identity);
    const mouseOperate = framework.service.registration.getInstance<MouseOperate>('MouseOperate');
    const sticky = useStickyInfo();

    return {
        /**
         * 移动之前的准备工作，用于存储一些值
         *
         * @param layerId - 图层 ID
         */
        beforeMove(layerId: string) {
            const layer = framework.service.flatLayer.getLayer(layerId, true);
            oldInfo.current = layer;
            originLayerInfo.current = framework.service.frame.exportLayerOriginInfo(layerId);
            framework.service.agentLayout.beforeDragLayer(layerId);
            framework.service.frame.__emitBeforeBaseOperate(layer, BaseLayerViewType.MOVE);
            sticky.before();
        },
        /**
         * 移动 —— 移动之前需要先确保 `beforeMove` 函数的调用
         *
         * @param layerId - 图层 ID
         * @param e - 触摸事件对象
         */
        move(layerId: string, e: ITouchKitEvent) {
            if (!oldInfo.current || !originLayerInfo.current) return;
            const moveLayer = framework.service.flatLayer.getLayer(layerId);
            if (!moveLayer) {
                mouseOperate.emitCancel();
                return;
            }

            if (e.isStart) {
                mouseOperate.emitMoveStart({
                    layerId,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
            }

            if (
                e.isEnd &&
                framework.service.frame.__emitBeforeLayerMoveEnd(layerId, {
                    x: e.originEvent.clientX,
                    y: e.originEvent.clientY,
                })
            ) {
                // 在跨画板吸附、Agent 编组和历史写入之前恢复，子图层沿用同一份初始坐标。
                const { originInfo, childOriginInfoMap } = originLayerInfo.current;
                framework.service.frame.updateLayerPosition(layerId, 0, 0, originInfo!, childOriginInfoMap, {
                    isStart: false,
                    isEnd: true,
                });
                framework.service.agentLayout.cancelDragLayer(layerId);
                // 宿主接收意味着取消画布移动，由新生命周期统一清理吸附、参考线及活动操作状态。
                mouseOperate.emitCancel();
                sticky.clear();
                framework.service.editor.setIsBaseOperate(false);
                framework.service.frame.setActiveLayer(layerId);
                framework.service.edgelessCanvas.requestRenderAll();
                oldInfo.current = undefined;
                originLayerInfo.current = undefined;
                return;
            }

            framework.service.editor.setIsBaseOperate(true);
            const scale = framework.service.edgelessCanvas.zoom;
            const rawX = e.displacementX / scale;
            const rawY = e.displacementY / scale;
            const candidates = new SnapCandidateResolver<MoveSnapSlot>({
                'move-x': rawX,
                'move-y': rawY,
            });
            mouseOperate.emitMoveBeforeUpdate({
                layerId,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                rawX,
                rawY,
                candidates,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });
            const x = candidates.get('move-x');
            const y = candidates.get('move-y');
            const { childOriginInfoMap, originInfo } = originLayerInfo.current;

            framework.service.frame.updateLayerPosition(layerId, x, y, originInfo!, childOriginInfoMap, {
                isStart: e.isStart,
                isEnd: e.isEnd,
            });
            framework.service.agentLayout.updateDragLayer(layerId);
            mouseOperate.emitMoveAfterUpdate({
                layerId,
                oldLayer: oldInfo.current,
                layer: mouseOperate.resolveCurrentOperateLayer(),
                appliedX: x,
                appliedY: y,
                pointerX: e.x,
                pointerY: e.y,
                isStart: e.isStart,
                isEnd: e.isEnd,
            });

            sticky.pending(layerId);

            if (e.isEnd) {
                const agentDragResult = framework.service.agentLayout.endDragLayer(layerId);
                const newInfo = framework.service.flatLayer.getLayer(layerId, true);
                sticky.clear();
                framework.service.editor.setIsBaseOperate(false);

                // 拖拉图片图层替换，会改变激活图层
                const activeId = framework.service.frame.getActivePickLayer()?.id;
                if (
                    (activeId && activeId === layerId) ||
                    framework.service.flatLayer.checkIsGroupExtension(oldInfo.current)
                ) {
                    const skipAgentArrangeHistory = !!agentDragResult?.skipHistory && agentDragResult.shouldArrange;
                    const retainOriginAgentGroup = !!agentDragResult?.retainOriginGroup;

                    if (agentDragResult?.packHistory) {
                        framework.service.history.write({
                            ...agentDragResult.packHistory,
                            trackFunc: trackConfig[LayerType.GROUP_LAYER]?.group,
                        });
                        framework.service.frame.setActiveLayer(layerId);
                        // console.log('写入历史记录 agent pack');
                    } else if (sticky.hasSticky() && !retainOriginAgentGroup) {
                        sticky.after(oldInfo.current);
                        // console.log('移入图层');
                    } else if (agentDragResult?.historyOldLayers && agentDragResult.historyNewLayers) {
                        const { historyOldLayers, historyNewLayers } = agentDragResult;

                        if (
                            historyOldLayers.length === 1 &&
                            historyNewLayers.length === 1 &&
                            historyOldLayers[0].id === historyNewLayers[0].id
                        ) {
                            framework.service.history.write(historyOldLayers[0], historyNewLayers[0]);
                        } else {
                            framework.service.history.write(historyOldLayers, historyNewLayers);
                        }
                        framework.service.frame.setActiveLayer(layerId);
                        // console.log('写入历史记录 1');
                    } else {
                        if (skipAgentArrangeHistory) {
                            // Agent group 内没有发生排序变化时，结束后只回弹，不写入无效移动历史。
                            console.log('结束后只回弹');
                        } else if (newInfo) {
                            framework.service.history.write(oldInfo.current, newInfo);
                            // console.log('写入历史记录 2');
                        } else {
                            framework.service.history.write(oldInfo.current);
                            // console.log('写入历史记录 3');
                        }
                        framework.service.frame.setActiveLayer(layerId);
                    }
                }
                framework.service.agentLayout.arrangeDragResult(agentDragResult);
                const currentActiveInfo = framework.service.frame.getActiveInfo();
                const currentActiveLayerId = currentActiveInfo?.info.id;
                const currentActivePickLayerId = currentActiveInfo?.info.pickId;

                if (
                    agentDragResult?.shouldArrange &&
                    (currentActiveLayerId === layerId || currentActivePickLayerId === layerId)
                ) {
                    framework.service.frame.setActiveLayer(layerId);
                }

                mouseOperate.emitMoveEnd({
                    layerId,
                    oldLayer: oldInfo.current,
                    layer: mouseOperate.resolveCurrentOperateLayer(),
                    appliedX: x,
                    appliedY: y,
                    pointerX: e.x,
                    pointerY: e.y,
                    isStart: e.isStart,
                    isEnd: e.isEnd,
                });
            }
        },
    };
};
